import { useOptionalWorkspace } from '@/workspace/context';
import { WorkspaceIcon } from '@/workspace/icons';
import { useChatCopy } from '@/lib/chat-i18n';
import { ActionOutcome } from './action-outcome';
import { ReadableFields } from './action-fields';
import { displayFields } from './action-approval-copy';
import type { ActionView } from './action-confirmation';
import { copyForTool, type ActionCopy } from './action-copy';
import { isStoreReadTool } from '@/workspace/storefront/read-request';

export function ActionDetails({ action, copy: shared }: { action: ActionView; copy: ActionCopy }) {
  const copy = copyForTool(shared, action.tool);
  const ws = useOptionalWorkspace();
  const ui = useChatCopy();
  // A shopping assistant change or paid read is shown in its first tab as what it makes or reads,
  // not as its fields.
  const storefront =
    ['change_storefront_design', 'change_storefront_knowledge'].includes(action.tool) ||
    isStoreReadTool(action.tool);
  const fields = displayFields(action, copy).filter(
    ([key]) =>
      !['name', 'title', 'description', 'instructions', 'payload', 'markdown'].includes(key) &&
      !(
        storefront &&
        ['changes', 'set', 'remove', 'store_id', 'parts', 'focus', 'url', 'market'].includes(key)
      ),
  );
  const references = (['file', 'source'] as const).flatMap((kind) => {
    const ids = action.args[`${kind}Ids`];
    return Array.isArray(ids)
      ? ids.filter((id): id is string => typeof id === 'string').map((id) => ({ kind, id }))
      : [];
  });
  return (
    <>
      {/* A Storefront read's outcome is its Request tab's started line, not a result here. */}
      {action.result !== undefined && !isStoreReadTool(action.tool) && (
        <div className="pc-action-confirmation-result">
          <strong>{ui.toolResult}</strong>
          <ActionOutcome action={action} openLabel={copy.open} />
        </div>
      )}
      <ReadableFields fields={fields} copy={copy} />
      <dl className="pc-action-draft-properties">
        {action.accountLabel && (
          <div>
            <dt>{ui.account}</dt>
            <dd>{action.accountLabel}</dd>
          </div>
        )}
        {action.organizationLabel && (
          <div>
            <dt>{copy.fields.scope}</dt>
            <dd>{action.organizationLabel}</dd>
          </div>
        )}
      </dl>
      {references.length > 0 && (
        <ul className="pc-action-reference-list">
          {references.map(({ kind, id }) => {
            const item =
              kind === 'file'
                ? ws?.data.files.find((file) => file.id === id)
                : ws?.data.sources.find((source) => source.id === id);
            const title = item && ('title' in item ? item.title : item.name);
            return (
              <li key={`${kind}:${id}`}>
                <button
                  type="button"
                  data-canvas-trigger
                  disabled={!item || !ws}
                  onClick={() => ws?.openCanvas({ kind, id })}
                >
                  <WorkspaceIcon name={kind === 'file' ? 'file' : 'knowledge'} size={16} />
                  <span>{title || ui.citationUnavailable}</span>
                  <WorkspaceIcon name="chevron" size={14} className="pc-action-reference-open" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
